/**
 * File Logger for Frontend
 * Logs to both console and sends to backend for file storage
 */

interface LogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  category: string;
  message: string;
  data?: any;
}

class FileLogger {
  private logBuffer: LogEntry[] = [];
  private flushInterval: NodeJS.Timeout | null = null;
  private readonly BUFFER_SIZE = 10;
  private readonly FLUSH_INTERVAL = 5000; // 5 seconds

  constructor() {
    // Start auto-flush interval
    this.flushInterval = setInterval(() => {
      this.flush();
    }, this.FLUSH_INTERVAL);

    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush();
      });
    }
  }

  private formatMessage(level: string, category: string, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const dataStr = data ? `\n   Data: ${JSON.stringify(data, null, 2).substring(0, 500)}` : '';
    return `[${timestamp}] [${level.toUpperCase()}] [${category}] ${message}${dataStr}`;
  }

  private async sendToBackend(logs: LogEntry[]) {
    try {
      // Send logs to MCP server for file writing
      const response = await fetch('https://vrestmcp.qastco.com/api/log/frontend', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ logs }),
      });

      if (!response.ok) {
        console.error('Failed to send logs to backend:', response.statusText);
      }
    } catch (error) {
      console.error('Error sending logs to backend:', error);
    }
  }

  private log(level: 'info' | 'warn' | 'error' | 'debug', category: string, message: string, data?: any) {
    const formattedMsg = this.formatMessage(level, category, message, data);

    // Log to console
    const consoleMethod = level === 'error' ? console.error :
                         level === 'warn' ? console.warn :
                         level === 'debug' ? console.debug : console.log;
    consoleMethod(formattedMsg);

    // Add to buffer
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
    };

    this.logBuffer.push(entry);

    // Flush if buffer is full
    if (this.logBuffer.length >= this.BUFFER_SIZE) {
      this.flush();
    }
  }

  private flush() {
    if (this.logBuffer.length === 0) return;

    const logsToSend = [...this.logBuffer];
    this.logBuffer = [];

    // Send to backend asynchronously
    this.sendToBackend(logsToSend);
  }

  // Public methods with visual separators
  info(category: string, message: string, data?: any) {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    this.log('info', category, message, data);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }

  warn(category: string, message: string, data?: any) {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    this.log('warn', category, message, data);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }

  error(category: string, message: string, data?: any) {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    this.log('error', category, message, data);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }

  debug(category: string, message: string, data?: any) {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    this.log('debug', category, message, data);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }

  destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
    this.flush();
  }
}

// Export singleton instance
export const fileLogger = new FileLogger();

// Convenience methods
export const logWebSocket = {
  connecting: (url: string) => fileLogger.info('WEBSOCKET', `🔌 Connecting to ${url}`),
  connected: (socketId: string, transport: string) =>
    fileLogger.info('WEBSOCKET', `✅ Connected - Socket ID: ${socketId}, Transport: ${transport}`),
  disconnected: (reason: string) => fileLogger.warn('WEBSOCKET', `❌ Disconnected - Reason: ${reason}`),
  error: (error: any) => fileLogger.error('WEBSOCKET', '❌ Connection error', error),
  joiningSession: (sessionId: string) => fileLogger.info('WEBSOCKET', `🚀 Joining session: ${sessionId}`),
  joinedSession: (sessionId: string) => fileLogger.info('WEBSOCKET', `✅ Joined session: ${sessionId}`),
  menuDisplayed: (sessionId: string, itemCount: number, category?: string) =>
    fileLogger.info('WEBSOCKET_EVENT', `📋 Menu displayed - Session: ${sessionId}, Items: ${itemCount}, Category: ${category || 'all'}`),
  orderCreated: (sessionId: string, orderNumber: string, total: number) =>
    fileLogger.info('WEBSOCKET_EVENT', `🛒 Order created - Session: ${sessionId}, Order: ${orderNumber}, Total: £${total}`),
};

export const logVoice = {
  sessionStarting: () => fileLogger.info('VOICE', '🎤 Starting voice session'),
  sessionStarted: (sessionId: string) => fileLogger.info('VOICE', `✅ Voice session started: ${sessionId}`),
  sessionEnded: (sessionId: string) => fileLogger.info('VOICE', `🛑 Voice session ended: ${sessionId}`),
  listening: () => fileLogger.info('VOICE', '👂 Listening...'),
  processing: () => fileLogger.info('VOICE', '🤔 Processing...'),
  transcript: (text: string) => fileLogger.info('VOICE', `💬 Transcript: "${text}"`),
  error: (error: any) => fileLogger.error('VOICE', '❌ Voice error', error),
};
